import { useEffect, useState, type FormEvent } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../../user-dashboard/auth/AuthContext';
import droneServiceService, {
  fieldServiceTitle,
  type DroneAnalysisMode,
  type DroneServiceType,
} from '../../../services/droneServiceService';

const PH_COUNTRY_CODE = '+63';

type DroneServiceRequestModalProps = {
  open: boolean;
  serviceType: DroneServiceType | null;
  analysisMode: DroneAnalysisMode;
  farmName?: string | null;
  onClose: () => void;
};

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** Digits only for PH local part (strip spaces/dashes; drop leading 0 from 09…). */
function normalizePhLocalNumber(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('63')) digits = digits.slice(2);
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

function buildPhPhoneE164(localRaw: string): string | null {
  const local = normalizePhLocalNumber(localRaw);
  // PH mobile: 10 digits starting with 9 (e.g. 9171234567)
  if (!/^9\d{9}$/.test(local)) return null;
  return `${PH_COUNTRY_CODE}${local}`;
}

export default function DroneServiceRequestModal({
  open,
  serviceType,
  analysisMode,
  farmName,
  onClose,
}: DroneServiceRequestModalProps) {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const isGuest = user?.is_system_guest === true;
  const accountEmail = !isGuest && user?.email ? user.email : '';

  const [email, setEmail] = useState('');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmail(accountEmail);
    setPhoneLocal('');
    setError(null);
    setSuccessMessage(null);
    setSubmitting(false);
  }, [open, accountEmail, serviceType]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  if (!open || !serviceType) return null;

  const emailLocked = Boolean(accountEmail);
  const shell = isDark
    ? 'rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl'
    : 'rounded-2xl border border-gray-200 bg-white shadow-2xl';
  const labelClass = isDark ? 'text-gray-300' : 'text-gray-700';
  const inputClass = isDark
    ? 'border-gray-600 bg-gray-800 text-white placeholder:text-gray-500 focus:border-emerald-500 focus:ring-emerald-500/30'
    : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-[#008540] focus:ring-[#008540]/30';
  const prefixClass = isDark
    ? 'border-gray-600 bg-gray-800/80 text-gray-200'
    : 'border-gray-300 bg-gray-50 text-gray-700';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    const fullPhone = buildPhPhoneE164(phoneLocal);

    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!fullPhone) {
      setError('Enter a valid Philippines mobile number (10 digits, e.g. 917 123 4567).');
      return;
    }

    setSubmitting(true);
    try {
      const res = await droneServiceService.submitInquiry({
        service_type: serviceType,
        email: trimmedEmail,
        phone: fullPhone,
        analysis_mode: analysisMode,
        farm_name: farmName?.trim() || undefined,
      });
      setSuccessMessage(res.message || 'Thanks — we received your request and will contact you soon.');
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not send your request. Please try again.';
      setError(typeof detail === 'string' ? detail : 'Could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="drone-service-request-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
        onClick={() => {
          if (!submitting) onClose();
        }}
        aria-label="Close"
      />
      <div className={`relative z-10 w-full max-w-md flex flex-col ${shell}`}>
        <div className={`flex items-start gap-3 border-b px-4 py-3.5 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
              isDark ? 'bg-emerald-950/60 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
            }`}
            aria-hidden
          >
            <i
              className={`${
                serviceType === 'ch4_awd' || serviceType === 'soil_nutrient'
                  ? 'ri-base-station-line'
                  : 'ri-drone-line'
              } text-xl`}
            />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h3
              id="drone-service-request-title"
              className={`text-base font-semibold leading-snug ${isDark ? 'text-white' : 'text-gray-900'}`}
            >
              {fieldServiceTitle(serviceType)}
            </h3>
            <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Leave your contact details and we will get back to you.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!submitting) onClose();
            }}
            className={`shrink-0 rounded-lg p-1.5 transition-colors ${
              isDark
                ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
            }`}
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {successMessage ? (
          <div className="px-4 py-5">
            <div
              className={`rounded-xl px-3 py-3 text-sm leading-relaxed ${
                isDark
                  ? 'bg-emerald-950/40 text-emerald-200 border border-emerald-800/60'
                  : 'bg-emerald-50 text-emerald-900 border border-emerald-200'
              }`}
            >
              <p className="font-medium">{successMessage}</p>
              <p className={`mt-2 text-xs ${isDark ? 'text-emerald-300/80' : 'text-emerald-800/80'}`}>
                A confirmation email was sent to your inbox, and our team has been notified.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`mt-4 w-full rounded-xl py-2.5 text-sm font-semibold transition-colors ${
                isDark
                  ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                  : 'bg-[#008540] text-white hover:opacity-90'
              }`}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-4 py-4 space-y-3.5">
            <label className="block">
              <span className={`mb-1.5 block text-xs font-medium ${labelClass}`}>
                Email {isGuest || !emailLocked ? <span className="text-red-500">*</span> : null}
              </span>
              <input
                type="email"
                autoComplete="email"
                required
                disabled={emailLocked || submitting}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={isGuest ? 'you@example.com' : 'Email address'}
                className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none ring-0 focus:ring-2 disabled:opacity-70 ${inputClass}`}
              />
              {isGuest ? (
                <span className={`mt-1 block text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  You are exploring as a guest — enter an email so we can reach you.
                </span>
              ) : null}
            </label>

            <label className="block">
              <span className={`mb-1.5 block text-xs font-medium ${labelClass}`}>
                Phone (Philippines) <span className="text-red-500">*</span>
              </span>
              <div className="flex">
                <span
                  className={`inline-flex shrink-0 items-center rounded-l-xl border border-r-0 px-3 text-sm font-semibold tabular-nums ${prefixClass}`}
                  aria-hidden
                >
                  {PH_COUNTRY_CODE}
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  required
                  disabled={submitting}
                  value={phoneLocal}
                  onChange={(e) => setPhoneLocal(e.target.value.replace(/[^\d\s]/g, ''))}
                  placeholder="917 123 4567"
                  maxLength={14}
                  className={`min-w-0 flex-1 rounded-r-xl border px-3 py-2.5 text-sm outline-none ring-0 focus:ring-2 disabled:opacity-70 ${inputClass}`}
                  aria-label="Philippines mobile number"
                />
              </div>
              <span className={`mt-1 block text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Country code +63 is fixed. Enter your 10-digit mobile number.
              </span>
            </label>

            {error ? (
              <p
                className={`rounded-lg px-3 py-2 text-xs ${
                  isDark ? 'bg-red-950/40 text-red-300' : 'bg-red-50 text-red-700'
                }`}
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                disabled={submitting}
                onClick={onClose}
                className={`flex-1 rounded-xl py-2.5 text-sm font-medium transition-colors ${
                  isDark
                    ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={`flex-1 rounded-xl py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${
                  isDark
                    ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                    : 'bg-[#008540] text-white hover:opacity-90'
                }`}
              >
                {submitting ? 'Sending…' : 'Submit request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
